import React from "react";
import { BookOpen, Lock, CheckCircle2, Shield, ChevronRight, ArrowLeft, Clock, ClipboardCheck } from "lucide-react";
import EquipmentChecklist from "./EquipmentChecklist";
import { COURSE_DATA } from "./courseData";

export default function CourseHub({ stateId, onSelectModule, onBack, isDark }) {
  const D = isDark;
  const stateData = COURSE_DATA[stateId];

  const bg      = D ? "#0d1f3c" : "#f4f7fb";
  const panel   = D ? "#142a4d" : "#ffffff";
  const mid     = D ? "#1a3358" : "#f0f4f8";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textMid = D ? "rgba(240,244,248,0.80)" : "#334155";
  const textSub = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  return (
    <div style={{ background: bg, minHeight: "60vh", padding: "clamp(16px,3vw,32px) clamp(14px,4vw,32px)", transition: "background .3s" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Back button */}
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 20, background: "none", border: "none", cursor: "pointer", color: textSub, fontSize: 14, fontWeight: 700, fontFamily: "inherit", padding: 0 }}>
          <ArrowLeft size={16} /> All States
        </button>

        {/* Hero banner */}
        <div style={{ background: "linear-gradient(135deg,#0d1f3c 0%,#1a3358 60%,#0f2d56 100%)", borderRadius: 20, padding: "clamp(24px,3vw,36px) clamp(20px,3vw,36px)", marginBottom: "clamp(20px,3vw,32px)", position: "relative", overflow: "hidden", border: "1px solid rgba(201,151,28,0.25)" }}>
          <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize: "18px 18px", pointerEvents: "none" }} />
          <div aria-hidden style={{ position: "absolute", top: "-20%", right: "-5%", opacity: 0.08, pointerEvents: "none" }}>
            <Shield size={200} color="white" />
          </div>
          <div style={{ position: "relative", zIndex: 1, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 20 }}>
            <div>
              <span style={{ display: "inline-block", background: "rgba(201,151,28,0.20)", border: "1px solid rgba(201,151,28,0.40)", borderRadius: 99, padding: "4px 14px", fontSize: 11, fontWeight: 800, color: gold, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 12 }}>
                Premium Certification Course
              </span>
              <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(22px,4vw,36px)", fontWeight: 900, color: "#fff", margin: "0 0 10px", lineHeight: 1.15 }}>
                {stateData.title} Crash Course
              </h1>
              <p style={{ fontSize: "clamp(13px,1.8vw,16px)", color: "rgba(255,255,255,0.68)", margin: 0, lineHeight: 1.7, maxWidth: 480 }}>{stateData.description}</p>
            </div>
            {/* Progress ring */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, background: "rgba(0,0,0,0.25)", borderRadius: 16, padding: "16px 20px", backdropFilter: "blur(8px)", flexShrink: 0 }}>
              <div style={{ position: "relative", width: 64, height: 64 }}>
                <svg width="64" height="64" viewBox="0 0 36 36">
                  <path fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path fill="none" stroke={gold} strokeWidth="3" strokeDasharray={`${stateData.progress}, 100`} strokeLinecap="round" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 900, color: goldL }}>{stateData.progress}%</div>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Overall Progress</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{stateData.modules.filter(m => m.status === "completed").length} of {stateData.modules.length} complete</div>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment Checklist — always visible at top */}
        <div style={{ background:panel, borderRadius:18, padding:"clamp(16px,2.5vw,24px)", marginBottom:"clamp(14px,2vw,20px)", border:`1px solid ${border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
            <div style={{ width:38, height:38, borderRadius:10, background:"rgba(201,151,28,0.15)", border:"1.5px solid rgba(201,151,28,0.30)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <ClipboardCheck size={18} style={{ color:gold }}/>
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:text }}>Pre-Trip Equipment Checklist</div>
              <div style={{ fontSize:12, color:textSub }}>Tap each item to verify your equipment — required before every move</div>
            </div>
          </div>
          <EquipmentChecklist stateId={stateId} isDark={D}/>
        </div>

        {/* Module grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,280px),1fr))", gap: "clamp(10px,2vw,16px)" }}>
          {stateData.modules.map((module, index) => {
            const locked = module.status === "locked";
            const done   = module.status === "completed";
            const isExam = module.isExam;

            return (
              <div key={module.id}
                onClick={() => !locked && onSelectModule(module.id)}
                style={{
                  background: locked ? (D ? "rgba(255,255,255,0.03)" : "#f8fafc") : panel,
                  borderRadius: 16,
                  border: isExam
                    ? `2px dashed rgba(201,151,28,0.50)`
                    : done
                    ? `1.5px solid rgba(5,150,105,0.35)`
                    : `1px solid ${border}`,
                  padding: "clamp(16px,2.5vw,22px)",
                  cursor: locked ? "not-allowed" : "pointer",
                  opacity: locked ? 0.55 : 1,
                  display: "flex", flexDirection: "column", gap: 12,
                  gridColumn: isExam ? "1 / -1" : "auto",
                  transition: "all .15s",
                  boxShadow: D ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(13,31,60,0.05)",
                }}
                onMouseEnter={e => { if (!locked) { e.currentTarget.style.borderColor = done ? "rgba(5,150,105,0.60)" : gold; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 20px rgba(0,0,0,${D?0.30:0.12})`; } }}
                onMouseLeave={e => { if (!locked) { e.currentTarget.style.borderColor = isExam ? "rgba(201,151,28,0.50)" : done ? "rgba(5,150,105,0.35)" : border; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = D ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(13,31,60,0.05)"; } }}
              >
                {/* Badge + status icon */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", color: isExam ? gold : locked ? textSub : done ? "#059669" : gold }}>
                    {isExam ? "🏁 Final Exam" : `Module ${index + 1}`}
                  </span>
                  {done   ? <CheckCircle2 size={18} style={{ color: "#059669" }} />
                  : locked ? <Lock size={16} style={{ color: textSub }} />
                  :          <BookOpen size={16} style={{ color: gold }} />}
                </div>

                {/* Title */}
                <div style={{ fontSize: "clamp(14px,2vw,17px)", fontWeight: 800, color: locked ? textSub : text, lineHeight: 1.3 }}>{module.title}</div>

                {/* Footer */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${border}`, marginTop: "auto" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: textSub }}>
                    <Clock size={12} /> {module.time}
                  </span>
                  {!locked && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 800, color: done ? "#059669" : gold }}>
                      {done ? "Review" : "Start"} <ChevronRight size={14} />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
